"use client"

import { useState, useEffect } from "react"
import axios from "axios"
import config from "../config"
import MapPicker from "../components/MapPicker"
import { validateImageFile } from "../utils/fileUtils"
import { SUPPORTED_CURRENCIES, COMMON_TIMEZONES, guessTimezone, currencySymbol, formatMoney } from "../utils/format"
import { useAuth } from "../context/AuthContext"
import { useNavigate } from "react-router-dom"
import { useSuccessFailure } from "../context/SuccessFailureContext"
import { Helmet } from 'react-helmet-async'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Search, Plus, Calendar, MapPin, Users, Clock, 
  ChevronLeft, ChevronRight, X, Image, Tag, 
  CreditCard, Eye, EyeOff, Loader2, Sparkles,
  Filter, Grid, List, TrendingUp, Star
} from 'lucide-react'

const MyEventsPage = () => {
  const eventTitle = process.env.REACT_APP_EVENT_TITLE || "EventSawa"
  const eventDescription = process.env.REACT_APP_EVENT_DESCRIPTION || "We make events sawa"
  const { accessToken } = useAuth()
  const { user, loading: authLoading, isLoggedIn } = useAuth();
  const [loading, setLoading] = useState(false)
  const [events, setEvents] = useState([])
  const [filteredEvents, setFilteredEvents] = useState([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [viewMode, setViewMode] = useState('grid') // 'grid' or 'list'
  const [sortBy, setSortBy] = useState('newest') // 'newest', 'oldest', 'name'
  const eventsPerPage = 8
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    location: "",
    location_url: "",
    latitude: null,
    longitude: null,
    category: "",
    keywords: "",
    date: "",
    time: "",
    timezone: guessTimezone(),
    thumbnail: null,
    visibility: "",
    // Payment related fields
    is_paid_event: false,
    currency: "KES",
    ticket_price: 0,
    payment_instructions: "",
    payment_deadline: "",
    // Optional engagement features
    enable_arrival_heatmap: true,
    enable_gamified_entry: false,
    enable_live_wall: true
  })
  const [error, setError] = useState("")
  const [summary, setSummary] = useState(null)
  const [categories, setCategories] = useState([])
  const [loadingCategories, setLoadingCategories] = useState(true)
  const { showSuccessModal, showFailureModal } = useSuccessFailure()
  const navigate = useNavigate()

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${config.API_BASE_URL}/events/categories/`);
        setCategories(response.data.categories);
      } catch (error) {
        // console.error('Error fetching categories:', error);
      } finally {
        setLoadingCategories(false);
      }
    };
    fetchCategories();
  }, []);

  // Fetch user's events
  useEffect(() => {
    const fetchEvents = async () => {
      if (!accessToken) return

      try {
        const response = await axios.get(`${config.API_BASE_URL}/events/my-events/`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        })
        setEvents(response.data) // Assuming response.data is an array of events
        setFilteredEvents(response.data)
      } catch (err) {
        // console.error("Error fetching events:", err)
        setEvents([]) // Reset events if an error occurs
        setFilteredEvents([])
      }
    }

    fetchEvents() // Fetch events when the component mounts or the accessToken changes
  }, [accessToken]) // Re-fetch events whenever the accessToken changes

  // Fetch cross-event organizer KPIs
  useEffect(() => {
    const fetchSummary = async () => {
      if (!accessToken) return
      try {
        const response = await axios.get(`${config.API_BASE_URL}/events/organizer/summary/`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        })
        setSummary(response.data)
      } catch (err) {
        // non-critical
      }
    }
    fetchSummary()
  }, [accessToken])

  // Build a compact revenue label across (possibly multiple) currencies.
  const revenueLabel = () => {
    const rev = summary?.revenue_by_currency || {}
    const entries = Object.entries(rev)
    if (entries.length === 0) return formatMoney(0, "KES")
    return entries.map(([cur, amt]) => formatMoney(amt, cur)).join(" + ")
  }

  // Filter and sort events based on search term and sort preference
  useEffect(() => {
    let filtered = [...events]
    
    // Filter by search term
    if (searchTerm.trim() !== "") {
      const searchLower = searchTerm.toLowerCase()
      filtered = filtered.filter((event) => {
        return (
          event.title.toLowerCase().includes(searchLower) ||
          event.description.toLowerCase().includes(searchLower) ||
          event.location.toLowerCase().includes(searchLower) ||
          (event.keywords && event.keywords.toLowerCase().includes(searchLower))
        )
      })
    }
    
    // Sort events
    console.log('Sorting by:', sortBy)
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'oldest':
          const dateA = new Date(a.created_at.replace(" ", "T"))
          const dateB = new Date(b.created_at.replace(" ", "T"))
          return dateA - dateB
        case 'name':
          return a.title.localeCompare(b.title)
        case 'newest':
        default:
          const dateC = new Date(a.created_at.replace(" ", "T"))
          const dateD = new Date(b.created_at.replace(" ", "T"))
          return dateD - dateC
      }
    })
    console.log('Sorted events:', filtered)
    
    setFilteredEvents(filtered)
    setCurrentPage(1) // Reset to first page when search or sort changes
  }, [searchTerm, events, sortBy])

  // Get current events for pagination
  const indexOfLastEvent = currentPage * eventsPerPage
  const indexOfFirstEvent = indexOfLastEvent - eventsPerPage
  const currentEvents = filteredEvents.slice(indexOfFirstEvent, indexOfLastEvent)
  const totalPages = Math.ceil(filteredEvents.length / eventsPerPage)

  // Handle card click to navigate to event details page
  const handleCardClick = (eventSlug) => {
    navigate(`/events/${eventSlug}`)
  }

  // Handle edit event
  const handleEditEvent = (eventSlug) => {
    setIsModalOpen(true)
    // You could pre-populate form with event data here
    // For now, just open the modal
  }

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value)
  }

  // Handle pagination
  const paginate = (pageNumber) => setCurrentPage(pageNumber)

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    })
  }

  // Get category color
  const getCategoryColor = (category) => {
    const colors = {
      'conference': 'bg-blue-100 text-blue-800',
      'workshop': 'bg-green-100 text-green-800',
      'party': 'bg-pink-100 text-pink-800',
      'sports': 'bg-orange-100 text-orange-800',
      'music': 'bg-purple-100 text-purple-800',
      'business': 'bg-gray-100 text-gray-800',
      'other': 'bg-indigo-100 text-indigo-800'
    }
    return colors[category?.toLowerCase()] || colors.other
  }

  // Handle modal form submission
  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    // Frontend validation
    if (!formData.thumbnail) {
      setError('Please upload a thumbnail photo');
      setLoading(false);
      return;
    }

    if (!formData.visibility || !['public', 'private'].includes(formData.visibility)) {
      setError('Please select event visibility');
      setLoading(false);
      return;
    }

    // Validate thumbnail file size before submission
    if (formData.thumbnail) {
      try {
        await validateImageFile(formData.thumbnail);
      } catch (error) {
        setError(error.message);
        setLoading(false);
        return;
      }
    }

    const form = new FormData()

    Object.keys(formData).forEach((key) => {
      if (key === "thumbnail" && formData[key]) {
        form.append("thumbnail_photo", formData[key], formData[key].name)
      } else if ((key === "latitude" || key === "longitude") &&
                 (formData[key] === null || formData[key] === "")) {
        // Skip empty coordinates so the backend FloatField doesn't get "null".
      } else {
        form.append(key, formData[key])
      }
    })

    try {
      const response = await axios.post(`${config.API_BASE_URL}/events/create/`, form, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "multipart/form-data",
        },
      })

      showSuccessModal("Event created successfully!") // Show success

      setEvents((prev) => [response.data, ...prev])
      setIsModalOpen(false)
    } catch (error) {
      // console.error("Error creating event:", error)
      const message = error.response?.data?.message || "Error creating event"
      setError(message)
      showFailureModal(message)
    } finally {
      setLoading(false) // Stop loading
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
      <Helmet>
        <title>{eventTitle} - My Events</title>
        <meta name="description" content={eventDescription} />
      </Helmet>

      {/* Header Section */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl">
                <Sparkles className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">My Events</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">Manage and create your events</p>
              </div>
            </div>

            {/* Stats */}
            <div className="flex items-center gap-4 sm:gap-6 flex-wrap">
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{events.length}</p>
                <p className="text-sm text-gray-500">Total Events</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-purple-600">{summary?.upcoming_events ?? '—'}</p>
                <p className="text-sm text-gray-500">Upcoming</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-indigo-600">{summary?.tickets_sold ?? '—'}</p>
                <p className="text-sm text-gray-500">Tickets Sold</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-600">{summary?.checked_in ?? '—'}</p>
                <p className="text-sm text-gray-500">Checked In</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">{summary ? revenueLabel() : '—'}</p>
                <p className="text-sm text-gray-500">Revenue (verified)</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Controls Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-4 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search events..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm"
              />
            </div>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="pl-4 pr-10 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm appearance-none"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="name">By Name</option>
            </select>
          </div>

          {/* View Controls */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-3 rounded-xl transition-colors ${
                viewMode === 'grid' 
                  ? 'bg-purple-600 text-white' 
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              } shadow-sm`}
            >
              <Grid className="h-5 w-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-3 rounded-xl transition-colors ${
                viewMode === 'list' 
                  ? 'bg-purple-600 text-white' 
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              } shadow-sm`}
            >
              <List className="h-5 w-5" />
            </button>
            <button
              onClick={() => setIsModalOpen(true)}
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl hover:from-purple-700 hover:to-indigo-700 transition-all shadow-lg flex items-center gap-2"
            >
              <Plus className="h-5 w-5" />
              <span className="hidden sm:inline">Create Event</span>
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
            <span className="ml-3 text-gray-600">Loading your events...</span>
          </div>
        ) : (
          <AnimatePresence mode="wait">
            {currentEvents.length > 0 ? (
              <motion.div
                key={viewMode}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className={
                  viewMode === 'grid' 
                    ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
                    : "space-y-4"
                }
              >
                {currentEvents.map((event, index) => (
                  <motion.div
                    key={event.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    whileHover={{ y: -4 }}
                    className={
                      viewMode === 'grid'
                        ? "bg-white dark:bg-gray-800 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer group"
                        : "bg-white dark:bg-gray-800 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer p-6 flex gap-6"
                    }
                    onClick={() => handleCardClick(event.slug)}
                  >
                    {viewMode === 'grid' ? (
                      <>
                        {/* Image */}
                        <div className="relative h-48 overflow-hidden">
                          <img
                            src={`${config.MEDIA_BASE_URL}${event.thumbnail_photo}`}
                            alt={event.title}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                          {/* Date badge */}
                          {event.date && (
                            <div className="absolute top-3 left-3 bg-white/95 dark:bg-gray-900/90 rounded-xl px-2.5 py-1.5 text-center shadow-md leading-none">
                              <div className="text-lg font-extrabold text-gray-900 dark:text-white">
                                {new Date(event.date).getDate()}
                              </div>
                              <div className="text-[10px] font-bold uppercase tracking-wide text-purple-600 dark:text-purple-400">
                                {new Date(event.date).toLocaleString('en-US', { month: 'short' })}
                              </div>
                            </div>
                          )}
                          <div className="absolute top-3 right-3">
                            <div className="p-2 bg-white/90 dark:bg-gray-900/70 backdrop-blur-sm rounded-full">
                              {event.visibility === 'private'
                                ? <EyeOff className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                                : <Eye className="h-4 w-4 text-gray-600 dark:text-gray-300" />}
                            </div>
                          </div>
                          <span className="absolute bottom-3 left-3 px-3 py-1 rounded-full bg-white/95 dark:bg-gray-900/85 text-gray-900 dark:text-white text-sm font-bold shadow-md">
                            {event.is_paid_event ? formatMoney(event.ticket_price || 0, event.currency) : 'Free'}
                          </span>
                        </div>

                        {/* Content */}
                        <div className="p-5">
                          <div className="flex items-start justify-between mb-3">
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white truncate group-hover:text-purple-600 transition-colors">
                              {event.title}
                            </h3>
                          </div>

                          <div className="space-y-2">
                            <div className="flex items-center text-sm text-gray-500">
                              <Calendar className="h-4 w-4 mr-2 flex-shrink-0" />
                              <span>{formatDate(event.date)}</span>
                            </div>
                            <div className="flex items-center text-sm text-gray-500">
                              <MapPin className="h-4 w-4 mr-2 flex-shrink-0" />
                              <span className="line-clamp-1">{event.location}</span>
                            </div>
                          </div>

                          {event.category && (
                            <div className="mt-3">
                              <span className={`inline-block px-3 py-1 text-xs font-medium rounded-full ${getCategoryColor(event.category)}`}>
                                {event.category}
                              </span>
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        {/* List View */}
                        <div className="relative w-48 h-32 overflow-hidden rounded-xl flex-shrink-0">
                          <img
                            src={`${config.MEDIA_BASE_URL}${event.thumbnail_photo}`}
                            alt={event.title}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                          <div className="absolute bottom-3 left-3 right-3">
                            <h4 className="text-white font-bold truncate">{event.title}</h4>
                          </div>
                        </div>

                        <div className="flex-1 space-y-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              {event.visibility === 'private' ? (
                                <EyeOff className="h-4 w-4 text-gray-400" />
                              ) : (
                                <Eye className="h-4 w-4 text-gray-400" />
                              )}
                              {event.is_paid_event && (
                                <div className="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full flex items-center gap-1">
                                  <CreditCard className="h-3 w-3" />
                                  {formatMoney(event.ticket_price || 0, event.currency)}
                                </div>
                              )}
                            </div>
                            {event.category && (
                              <span className={`px-3 py-1 text-xs font-medium rounded-full ${getCategoryColor(event.category)}`}>
                                {event.category}
                              </span>
                            )}
                            {/* Edit Button */}
                            <button
                              onClick={() => handleEditEvent(event.slug)}
                              className="absolute top-3 right-3 p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-lg"
                              title="Edit Event"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H4a2 2 0 01-2h4a2 2 0 012 2v4a2 2 0 012 2H4a2 2 0 01-2 2V6a2 2 0 01-2 2H4a2 2 0 00-2 2V6a2 2 0 01-2 2h-2a2 2 0 00-2 2V6a2 2 0 01-2 2z" />
                              </svg>
                            </button>
                          </div>

                          <p className="text-gray-600 line-clamp-3">{event.description}</p>

                          <div className="flex items-center gap-4 text-sm text-gray-500">
                            <div className="flex items-center">
                              <Calendar className="h-4 w-4 mr-1" />
                              {formatDate(event.date)}
                            </div>
                            <div className="flex items-center">
                              <MapPin className="h-4 w-4 mr-1" />
                              <span className="line-clamp-1">{event.location}</span>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </motion.div>
                ))}
              </motion.div>

              ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center py-20"
              >
                <div className="max-w-md mx-auto">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Calendar className="h-10 w-10 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {searchTerm ? "No events found" : "No events yet"}
                  </h3>
                  <p className="text-gray-500 mb-6">
                    {searchTerm 
                      ? "Try adjusting your search terms or filters"
                      : "Create your first event to get started"
                    }
                  </p>
                  {!searchTerm && (
                    <button
                      onClick={() => setIsModalOpen(true)}
                      className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl hover:from-purple-700 hover:to-indigo-700 transition-all shadow-lg inline-flex items-center gap-2"
                    >
                      <Plus className="h-5 w-5" />
                      Create Your First Event
                    </button>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        )}

        {/* Enhanced Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center mt-12">
            <nav className="flex items-center space-x-2">
              <button
                onClick={() => paginate(currentPage > 1 ? currentPage - 1 : 1)}
                disabled={currentPage === 1}
                className={`p-3 rounded-xl transition-all ${
                  currentPage === 1
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-white text-gray-700 hover:bg-purple-50 hover:text-purple-600 shadow-sm"
                }`}
              >
                <ChevronLeft className="h-5 w-5" />
              </button>

              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum
                if (totalPages <= 5) {
                  pageNum = i + 1
                } else if (currentPage <= 3) {
                  pageNum = i + 1
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i
                } else {
                  pageNum = currentPage - 2 + i
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => paginate(pageNum)}
                    className={`px-4 py-3 rounded-xl font-medium transition-all ${
                      currentPage === pageNum
                        ? "bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg"
                        : "bg-white text-gray-700 hover:bg-purple-50 hover:text-purple-600 shadow-sm"
                    }`}
                  >
                    {pageNum}
                  </button>
                )
              })}

              <button
                onClick={() => paginate(currentPage < totalPages ? currentPage + 1 : totalPages)}
                disabled={currentPage === totalPages}
                className={`p-3 rounded-xl transition-all ${
                  currentPage === totalPages
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-white text-gray-700 hover:bg-purple-50 hover:text-purple-600 shadow-sm"
                }`}
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </nav>
          </div>
        )}
      

      {/* Add Event Modal */}
      {isModalOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setIsModalOpen(false)}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ type: "spring", damping: 20, stiffness: 300 }}
            className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-4xl shadow-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4 text-white">
              <div className="flex items-center justify-between">
                <h3 className="text-2xl font-bold text-center flex-1">Create New Event</h3>
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              <p className="text-purple-100 text-center">Fill in the details below to create your amazing event</p>
            </div>

            <form onSubmit={handleSubmit} className="px-6 py-6 space-y-6">
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-xl p-4">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <Sparkles className="h-5 w-5 mr-2 text-purple-600" />
                    Event Details
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div key="title" className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 capitalize mb-2">
                        Event Title
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={formData.title}
                          onChange={(e) => setFormData({ ...formData, title: e.target.value.trimStart() })}
                          required
                          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          placeholder="Give your event a catchy title..."
                        />
                      </div>
                    </div>
                  <div key="description" className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                    <textarea
                      name="description"
                      rows={8}
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value.trimStart() })}
                      required
                      className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    />
                  </div>
                  <div key="category">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 capitalize mb-1">
                      Category
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      required
                      className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="" disabled>Select a category</option>
                      {categories.map((category) => (
                        <option key={category.value} value={category.value}>
                          {category.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div key="keywords" className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 capitalize mb-1">
                      Keywords
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {formData.keywords ? formData.keywords.split(',').map((keyword, index) => (
                        <div
                          key={index}
                          className="flex items-center bg-gray-200 px-3 py-1 rounded-full text-sm"
                          style={{
                            minWidth: '100px',
                            maxWidth: '200px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          <span className="mr-2 truncate">{keyword.trim()}</span>
                          <button
                            type="button"
                            onClick={() => {
                              const currentKeywords = formData.keywords.split(',');
                              const newKeywords = currentKeywords.filter(k => k.trim() !== keyword.trim());
                              setFormData({ ...formData, keywords: newKeywords.join(',') });
                            }}
                            className="text-gray-500 hover:text-gray-700"
                            title="Remove keyword"
                          >
                            ×
                          </button>
                        </div>
                      )) : null}
                    </div>
                    <div className="relative">
                      <input
                        type="text"
                        name="keywords"
                        value={formData.keywords}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Split by commas only (keep spaces within keywords)
                          const keywords = value.split(',');
                          // Remove empty keywords
                          const filteredKeywords = keywords.filter(k => k.trim().length > 0);
                          // Join with comma (no space)
                          setFormData({ ...formData, keywords: value }); // Use raw value instead of joining
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ',') {
                            e.preventDefault();
                            const currentValue = e.target.value;

                            // Split by commas only (keep spaces)
                            const keywords = currentValue.split(',');
                            const lastKeyword = keywords[keywords.length - 1];

                            // Validate the last keyword
                            const errors = [];

                            // Check total number of keywords
                            if (keywords.length > 4) {
                              errors.push('Maximum 4 keywords allowed');
                            }

                            // Check length of each keyword
                            const longKeywords = keywords.filter(k => k.trim().length > 50);
                            if (longKeywords.length > 0) {
                              errors.push('Each keyword cannot be longer than 50 characters');
                            }

                            if (errors.length > 0) {
                              const errorDiv = document.getElementById('keywordError');
                              if (errorDiv) {
                                errorDiv.textContent = errors.join('\n');
                              }
                              return;
                            }

                            // If valid, add comma
                            if (currentValue && !currentValue.endsWith(',')) {
                              e.target.value = currentValue + ',';
                              setFormData({ ...formData, keywords: currentValue + ',' });
                            }
                          }
                        }}
                        required
                        className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter keywords (comma separated)"
                      />
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                        <button
                          type="button"
                          onClick={() => {
                            const input = document.querySelector('input[name="keywords"]');
                            if (input) {
                              const value = input.value.trim();
                              if (value && !value.endsWith(',')) {
                                input.value = value + ',';
                                setFormData({ ...formData, keywords: value + ',' });
                              }
                            }
                          }}
                          className="text-gray-500 hover:text-gray-700"
                          title="Add comma"
                        >
                          ,
                        </button>
                      </div>
                    </div>
                    <div className="text-sm text-red-500 h-4" id="keywordError"></div>
                    <p className="text-sm text-gray-500">
                      Maximum 4 keywords, each up to 50 characters
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-gray-700 dark:text-gray-200">Timing Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date</label>
                    <input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      required
                      className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Time</label>
                    <input
                      type="time"
                      value={formData.time}
                      onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                      required
                      className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Timezone</label>
                    <select
                      value={formData.timezone}
                      onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                      className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {COMMON_TIMEZONES.map((tz) => (
                        <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">Attendees see times in this timezone.</p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-gray-700 dark:text-gray-200">Location Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div key="location">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 capitalize mb-1">
                      Location
                    </label>
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value.trimStart() })}
                      required
                      className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div key="location_url">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Location URL</label>
                    <input
                      type="text"
                      value={formData.location_url}
                      onChange={(e) => setFormData({ ...formData, location_url: e.target.value.trimStart() })}
                      required
                      className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Pin the venue on the map <span className="text-gray-400">(optional — powers "near me" recommendations)</span>
                  </label>
                  <MapPicker
                    lat={formData.latitude}
                    lng={formData.longitude}
                    onPick={(lat, lng) => setFormData({ ...formData, latitude: lat, longitude: lng })}
                  />
                  {formData.latitude != null && (
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Pinned at {Number(formData.latitude).toFixed(5)}, {Number(formData.longitude).toFixed(5)} ·{" "}
                      <button
                        type="button"
                        className="text-blue-600 dark:text-blue-400 underline"
                        onClick={() => setFormData({ ...formData, latitude: null, longitude: null })}
                      >
                        clear
                      </button>
                    </p>
                  )}
                </div>
              </div>

              {/* Payment Settings */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-gray-700 dark:text-gray-200">Payment Settings</h4>
                <div className="grid grid-cols-1 gap-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="isPaidEvent"
                      checked={formData.is_paid_event || false}
                      onChange={(e) => setFormData({ ...formData, is_paid_event: e.target.checked })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="isPaidEvent" className="ml-2 block text-sm text-gray-700">
                      This is a paid event
                    </label>
                  </div>

                  {formData.is_paid_event && (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Currency</label>
                          <select
                            value={formData.currency}
                            onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                            className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            {SUPPORTED_CURRENCIES.map((c) => (
                              <option key={c.code} value={c.code}>{c.label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="sm:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Ticket Price ({currencySymbol(formData.currency)})
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={formData.ticket_price || ''}
                            onChange={(e) => setFormData({ ...formData, ticket_price: parseFloat(e.target.value) || 0 })}
                            className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Enter base ticket price"
                          />
                          <p className="mt-1 text-xs text-gray-500">You can add VIP/Early-Bird tiers after creating the event.</p>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Payment Instructions
                        </label>
                        <textarea
                          rows="3"
                          value={formData.payment_instructions || ''}
                          onChange={(e) => setFormData({ ...formData, payment_instructions: e.target.value })}
                          className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., Paybill: 123456, Account: Event Name"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Payment Deadline
                        </label>
                        <input
                          type="datetime-local"
                          value={formData.payment_deadline || ''}
                          onChange={(e) => setFormData({ ...formData, payment_deadline: e.target.value })}
                          className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                          min={new Date().toISOString().slice(0, 16)}
                        />
                        <p className="mt-1 text-sm text-gray-500">
                          Leave empty if there's no specific deadline
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Optional engagement features */}
              <div className="space-y-3">
                <h4 className="text-lg font-semibold text-gray-700 dark:text-gray-200">Engagement (optional)</h4>
                {[
                  { k: 'enable_arrival_heatmap', label: 'Show arrival heatmap in analytics' },
                  { k: 'enable_gamified_entry', label: 'Enable lucky-draw / raffle at check-in' },
                  { k: 'enable_live_wall', label: 'Allow big-screen live check-in wall' },
                ].map(({ k, label }) => (
                  <label key={k} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <input type="checkbox" checked={!!formData[k]}
                      onChange={(e) => setFormData({ ...formData, [k]: e.target.checked })}
                      className="h-4 w-4 text-purple-600 rounded border-gray-300" />
                    {label}
                  </label>
                ))}
              </div>

              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-gray-700 dark:text-gray-200">Event Thumbnail</h4>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block">
                      <span className="text-gray-700">Upload Thumbnail</span>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/gif"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            try {
                              validateImageFile(file);
                              setFormData({ ...formData, thumbnail: file });
                            } catch (error) {
                              setError(error.message);
                              e.target.value = '';
                            }
                          }
                        }}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4
                                  file:rounded-full file:border-0
                                  file:text-sm file:font-semibold
                                  file:bg-blue-50 file:text-blue-700
                                  hover:file:bg-blue-100"
                      />
                    </label>

                    {formData.thumbnail && (
                      <div className="mt-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h5 className="text-sm font-medium text-gray-700">Preview</h5>
                            <p className="text-xs text-gray-500">{formData.thumbnail.name}</p>
                            <p className="text-xs text-gray-500">{(formData.thumbnail.size / (1024 * 1024)).toFixed(2)}MB</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setFormData({ ...formData, thumbnail: null });
                              document.querySelector('input[type="file"]').value = '';
                            }}
                            className="text-red-500 hover:text-red-700 text-sm"
                          >
                            Remove
                          </button>
                        </div>
                        <div className="mt-2">
                          <img
                            src={URL.createObjectURL(formData.thumbnail)}
                            alt="Thumbnail Preview"
                            className="w-32 h-32 object-cover rounded-lg"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      <span className="font-medium">Requirements:</span>
                      <br />
                      • Maximum file size: 10MB<br />
                      • Supported formats: JPEG, PNG, GIF<br />
                      • Recommended dimensions: 16:9 aspect ratio
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Event Visibility</label>
                    <select
                      value={formData.visibility}
                      onChange={(e) => setFormData({ ...formData, visibility: e.target.value })}
                      className="w-full border border-gray-300 rounded-md p-2"
                    >
                      <option value="" disabled>Select visibility</option>
                      <option value="public">Open to Public</option>
                      <option value="private">Private Event</option>
                    </select>
                  </div>
                </div>
                </div>
              </div>

              {error && <p className="text-red-500 text-sm">{error}</p>}

              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 bg-gray-300 rounded-md hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || !formData.thumbnail || !formData.visibility}
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {loading ? "Creating..." : "Create Event"}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </div>
    </div>
  )
}

export default MyEventsPage
